import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "expo-router";
import { useForm } from "react-hook-form";
import { JobVenueFeedbackFormValues, defaultJobVenueFeedbackValues, jobVenueFeedbackSchema, mapVenueFeedbackForBackend } from "./venue-feedback-types";
import { Card, CardContent } from "../ui/card";
import { Form, FormField, FormLabel, Textarea, Button, FormMessage } from "../ui";
import { View } from "react-native";
import { trpc } from "@/trpc/react";
import Toast from "react-native-toast-message";
import { P } from "../ui/typography";
import { useCrew } from "@/context/CrewProvider";

interface VenueFeedbackFormProps {
  jobVenueId: number;
  jobDateId: number;
}

export default function VenueFeedbackForm({ jobVenueId, jobDateId }: VenueFeedbackFormProps) {
  const router = useRouter();

  const form = useForm<JobVenueFeedbackFormValues>({
    resolver: zodResolver(jobVenueFeedbackSchema),
    defaultValues: defaultJobVenueFeedbackValues,
  });

  const { mutate: createVenueFeedback } = trpc.venueFeedback.create.useMutation({
    onSuccess(_response) {
      router.back();
      Toast.show({
        type: 'success',
        text1: 'Venue feedback successfully submitted!'
      });
    },
    onError(error) {
      Toast.show({
        type: 'error',
        text1: 'Failed to submit venue feedback',
        text2: error.message
      });
    }
  });

  const { currentCrewMember } = useCrew();
  const authorID = currentCrewMember?.id || 0;

  async function onSubmit(data: JobVenueFeedbackFormValues) {
    const mappedValues = mapVenueFeedbackForBackend(
      { ...data, job_venue_id: jobVenueId, job_date_id: jobDateId },
      authorID
    );
    createVenueFeedback(mappedValues);
  }

  return (
    <Form {...form}>
      <Card className='mt-4 mb-4'>
        <CardContent className="gap-6">
          <View className="gap-4">
            <P className="text-sm text-muted-foreground mt-4">
              Please provide detailed feedback about the venue, including any issues with facilities,
              equipment, accessibility, or other concerns that could affect future jobs at this location.
            </P>

            <FormField
              control={form.control}
              name="feedback"
              render={({ field }) => (
                <View className="gap-2">
                  <FormLabel>Venue Feedback</FormLabel>
                  <Textarea
                    {...field}
                    className="min-h-[120px]"
                    onChangeText={(value) => {
                      field.onChange(value);
                    }}
                    value={field.value || ''}
                  />
                  <FormMessage />
                </View>
              )}
            />
          </View>

          <Button
            onPress={form.handleSubmit(onSubmit)}
            className="mt-6"
            variant="crew"
          >
            Submit Venue Feedback
          </Button>
        </CardContent>
      </Card>
    </Form>
  );
}
